/**
 * Owner: Masa Bayu
 * File: qxp-package-integrity.ts
 * Date:Time: 2026-09-21 23:28
 * Description: PG3-05 — QXP package integrity gate · reject stubs · fail-closed · lineage.
 */

import { isQXPPackage, validateQXPPackage, type QXPPackage } from '@qx/qxp-core';

/** Legacy CDN identity stub from GAP-O03-01 — not a QXP v1 package. */
export function isQxpPackageIdentityStub(body: unknown): boolean {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return false;
  const record = body as Record<string, unknown>;
  return record.kind === 'qxp_package_object';
}

export type QxpPackageIntegrityResult =
  | {
      readonly ok: true;
      readonly package: QXPPackage;
      readonly qxpPublicationRef: string;
      readonly publicationId: string;
      readonly qxpVersion: '1.0.0';
      readonly rejectedStub: false;
      readonly mutatesQxp: false;
      readonly mutatesQxw: false;
      readonly inventsIam: false;
    }
  | {
      readonly ok: false;
      readonly reason: string;
      readonly rejectedStub: boolean;
      readonly mutatesQxp: false;
      readonly mutatesQxw: false;
      readonly inventsIam: false;
    };

/**
 * Validate CDN/package bytes as a real QXP v1.0.0 package.
 * Missing / stub / invalid → fail-closed.
 */
export function assertProductionQxpPackageIntegrity(input: {
  readonly body: unknown;
  readonly expectedQxpPublicationRef?: string;
  readonly expectedPublicationId?: string;
  readonly expectedIntegrityHash?: string;
}): QxpPackageIntegrityResult {
  const deny = (reason: string, rejectedStub: boolean): QxpPackageIntegrityResult =>
    Object.freeze({
      ok: false as const,
      reason,
      rejectedStub,
      mutatesQxp: false as const,
      mutatesQxw: false as const,
      inventsIam: false as const,
    });

  if (input.body === null || input.body === undefined) {
    return deny('Missing QXP package body — fail closed', false);
  }

  if (isQxpPackageIdentityStub(input.body)) {
    return deny(
      'CDN identity stub (kind=qxp_package_object) is not a QXP v1.0.0 package — fail closed',
      true,
    );
  }

  if (!isQXPPackage(input.body)) {
    const validation = validateQXPPackage(input.body);
    const first = validation.errors[0];
    return deny(
      first?.message ?? 'Invalid QXP package — fail closed',
      false,
    );
  }

  const pkg = input.body;
  if (pkg.version !== '1.0.0' || pkg.format !== 'QXP') {
    return deny('QXP package must be format QXP version 1.0.0 — fail closed', false);
  }

  const publicationId = pkg.publicationIdentity.publicationId;
  if (
    input.expectedPublicationId !== undefined &&
    publicationId !== input.expectedPublicationId
  ) {
    return deny('QXP publicationIdentity.publicationId mismatch — fail closed', false);
  }

  const sourceRevision = pkg.integrity.sourceRevision;
  if (
    input.expectedQxpPublicationRef !== undefined &&
    sourceRevision !== input.expectedQxpPublicationRef
  ) {
    return deny(
      'QXP integrity.sourceRevision must equal catalog qxpPublicationRef — fail closed',
      false,
    );
  }

  if (input.expectedIntegrityHash !== undefined) {
    const checksum = pkg.integrity.checksum;
    if (checksum !== input.expectedIntegrityHash) {
      return deny('QXP integrity.checksum mismatch with catalog — fail closed', false);
    }
  }

  return Object.freeze({
    ok: true as const,
    package: pkg,
    qxpPublicationRef: sourceRevision ?? publicationId,
    publicationId,
    qxpVersion: '1.0.0' as const,
    rejectedStub: false as const,
    mutatesQxp: false as const,
    mutatesQxw: false as const,
    inventsIam: false as const,
  });
}

export type QxpPackageLineageProof = {
  readonly ok: boolean;
  readonly updateKeepsPriorPackage: boolean;
  readonly updateNewRefDistinct: boolean;
  readonly newEditionDistinctContent: boolean;
  readonly priorUnchanged: boolean;
  readonly mutatesQxp: false;
  readonly mutatesQxw: false;
};

/**
 * Prove Update/New Edition package lineage does not mutate prior immutable packages.
 */
export function proveQxpPackageLineageIntact(input: {
  readonly priorPackageJson: string;
  readonly priorQxpPublicationRef: string;
  readonly priorPublicationId: string;
  readonly updatePackageJson: string;
  readonly updateQxpPublicationRef: string;
  readonly editionPackageJson: string;
  readonly editionPublicationId: string;
  /** Snapshot of prior bytes captured before Update/Edition emit. */
  readonly priorSnapshotBeforeMutation: string;
}): QxpPackageLineageProof {
  const prior = assertProductionQxpPackageIntegrity({
    body: JSON.parse(input.priorPackageJson) as unknown,
    expectedQxpPublicationRef: input.priorQxpPublicationRef,
    expectedPublicationId: input.priorPublicationId,
  });
  const update = assertProductionQxpPackageIntegrity({
    body: JSON.parse(input.updatePackageJson) as unknown,
    expectedQxpPublicationRef: input.updateQxpPublicationRef,
    expectedPublicationId: input.priorPublicationId,
  });
  const edition = assertProductionQxpPackageIntegrity({
    body: JSON.parse(input.editionPackageJson) as unknown,
    expectedPublicationId: input.editionPublicationId,
  });

  const priorUnchanged = input.priorPackageJson === input.priorSnapshotBeforeMutation;

  const updateKeepsPriorPackage =
    prior.ok &&
    update.ok &&
    priorUnchanged &&
    input.priorQxpPublicationRef !== input.updateQxpPublicationRef;

  const updateNewRefDistinct =
    prior.ok &&
    update.ok &&
    input.priorQxpPublicationRef !== input.updateQxpPublicationRef &&
    input.priorPackageJson !== input.updatePackageJson;

  const newEditionDistinctContent =
    edition.ok &&
    edition.publicationId === input.editionPublicationId &&
    edition.publicationId !== input.priorPublicationId;

  return Object.freeze({
    ok:
      prior.ok &&
      update.ok &&
      edition.ok &&
      updateKeepsPriorPackage &&
      updateNewRefDistinct &&
      newEditionDistinctContent &&
      priorUnchanged,
    updateKeepsPriorPackage,
    updateNewRefDistinct,
    newEditionDistinctContent,
    priorUnchanged,
    mutatesQxp: false as const,
    mutatesQxw: false as const,
  });
}
